/**
 * Feb 2026 — PWS Partner Landing Pages — Admin CRUD (Phase 1).
 *
 * Route: /admin/pws-partner-landing
 *
 * Admin-only. Manages the new `pws_partner_landing_pages` collection.
 * Links each landing page to an existing PWS franchisee partner record
 * (partner_type=pws_franchisee, pws_partner_status=active). The
 * partner's `partner_slug` (e.g. "PWS-NOOSA") is denormalised into
 * every booking CTA on the public page via `/book-water-test?p=...`
 * — the SAME param the existing attribution engine already consumes.
 * No new tracking system is introduced.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import StorefrontFieldsPanel from "@/components/admin/StorefrontFieldsPanel";
import {
  ArrowLeft, Plus, Pencil, Trash2, RefreshCw, ExternalLink, Copy,
  QrCode, CheckCircle2, XCircle, Archive, Send, BarChart3, Download,
  Loader2, MousePointerClick, Eye,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { fetchAndOpenAuthed } from "@/lib/authedDownload";

const STATUS_CHIP = {
  draft:     { label: "Draft",     cls: "bg-slate-100 text-slate-700 border-slate-300" },
  published: { label: "Published", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  archived:  { label: "Archived",  cls: "bg-amber-100 text-amber-800 border-amber-300" },
};

function StatusChip({ status }) {
  const s = STATUS_CHIP[status] || STATUS_CHIP.draft;
  return (
    <span data-testid={`pws-partner-landing-status-${status}`} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wider ${s.cls}`}>
      {s.label}
    </span>
  );
}

function publicUrl(slug) {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/pws-partner/${slug}`;
}

// SMS-friendly short link — /p/{slug} redirects to the full storefront.
// Short enough that SMS apps never truncate or mangle it.
function shortUrl(slug) {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/p/${slug}`;
}

function LandingForm({ initial, availablePartners, onCancel, onSaved }) {
  const isEdit = !!(initial && initial.id);
  const [form, setForm] = useState(() => ({
    slug:                 initial?.slug || "",
    partner_id:           initial?.partner_id || "",
    business_name:        initial?.business_name || "",
    region_label:         initial?.region_label || "",
    service_suburbs:      (initial?.service_suburbs || []).join(", "),
    phone_display:        initial?.phone_display || "",
    email_display:        initial?.email_display || "",
    hero_intro_override:  initial?.hero_intro_override || "",
    // SCWT-STOREFRONT-BRAND-HERO · Feb 2026 — Admin-editable brand
    // headline + subheadline. Blank falls back to the global AWARE
    // brand copy shown on every storefront.
    hero_headline:        initial?.hero_headline || "",
    hero_subheadline:     initial?.hero_subheadline || "",
    franchisee_name:      initial?.franchisee_name || "",
    franchisee_photo_url: initial?.franchisee_photo_url || "",
    // SCWT-HERO-BG · Feb 2026 — optional hero background image.
    hero_bg_url:          initial?.hero_bg_url || "",
    testimonial_quote:    initial?.testimonial_quote || "",
    testimonial_author:   initial?.testimonial_author || "",
    // Feb 2026 storefront rebuild — new admin-editable fields.
    years_in_business:    initial?.years_in_business ?? null,
    experience_bio:       initial?.experience_bio || "",
    why_choose_bio:       initial?.why_choose_bio || "",
    google_place_id:      initial?.google_place_id || "",
    services_offered:     initial?.services_offered || {},
    testimonials:         initial?.testimonials || [],
    faqs:                 initial?.faqs || [],
    gallery_before_after: initial?.gallery_before_after || [],
    status:               initial?.status || "draft",
  }));
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickPartner = (partnerId) => {
    const partner = availablePartners.find((p) => p.id === partnerId);
    setForm((f) => ({
      ...f,
      partner_id: partnerId,
      business_name: f.business_name || partner?.business_name || "",
      region_label:  f.region_label  || [partner?.franchise_region, partner?.state].filter(Boolean).join(", "),
    }));
  };

  // Feb 2026 · Option A — "Copy from partner account". Pulls the
  // partner's operational service_suburbs (managed on the partner
  // Territory tab) into the landing page marketing copy. Guarded
  // by a confirmation when the field already contains text, so a
  // click can't silently clobber a curated marketing list.
  const selectedPartner = availablePartners.find((p) => p.id === form.partner_id);
  const partnerAccountSuburbs = selectedPartner?.service_suburbs || [];
  const canCopyFromPartner =
    !!form.partner_id && partnerAccountSuburbs.length > 0;
  const copySuburbsFromPartner = () => {
    if (!canCopyFromPartner) return;
    const next = partnerAccountSuburbs.join(", ");
    if (form.service_suburbs.trim() && form.service_suburbs.trim() !== next) {
      if (!window.confirm(
        "Overwrite the current suburb list with the partner's account suburbs?\n\n" +
        `Current: ${form.service_suburbs}\n\nPartner account: ${next}`
      )) return;
    }
    setField("service_suburbs", next);
    toast.success(`Copied ${partnerAccountSuburbs.length} suburb${partnerAccountSuburbs.length === 1 ? "" : "s"} from partner account`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        service_suburbs: form.service_suburbs, // server normalises string → list
      };
      if (isEdit) {
        const { data } = await api.put(`/admin/pws-partner-landing/pages/${initial.id}`, payload);
        toast.success("Landing page updated");
        onSaved?.(data);
      } else {
        const { data } = await api.post("/admin/pws-partner-landing/pages", payload);
        toast.success("Landing page created");
        onSaved?.(data);
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid="pws-partner-landing-form"
      onSubmit={submit}
      className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Slug (URL segment)</label>
          <input
            data-testid="pws-partner-landing-input-slug"
            value={form.slug}
            onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            placeholder="noosa"
            maxLength={64}
            required
          />
          <div className="mt-1 text-xs text-slate-500">
            Public URL: <code className="text-[11px] bg-slate-100 px-1 rounded">/pws-partner/{form.slug || "…"}</code>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">PWS partner</label>
          <select
            data-testid="pws-partner-landing-input-partner"
            value={form.partner_id}
            onChange={(e) => onPickPartner(e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
            required
          >
            <option value="">— Choose a PWS partner —</option>
            {availablePartners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.business_name} · {p.partner_slug}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Business name (display)</label>
          <input
            data-testid="pws-partner-landing-input-business-name"
            value={form.business_name}
            onChange={(e) => setField("business_name", e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            maxLength={200}
            required
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Region label</label>
          <input
            data-testid="pws-partner-landing-input-region"
            value={form.region_label}
            onChange={(e) => setField("region_label", e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            placeholder="Sunshine Coast, QLD"
            maxLength={120}
          />
        </div>
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Service suburbs (comma-separated)</label>
            <button
              type="button"
              data-testid="pws-partner-landing-copy-suburbs-from-partner"
              onClick={copySuburbsFromPartner}
              disabled={!canCopyFromPartner}
              title={
                !form.partner_id
                  ? "Pick a partner first"
                  : partnerAccountSuburbs.length === 0
                    ? "Selected partner has no suburbs set on their account"
                    : `Copy ${partnerAccountSuburbs.length} suburbs from partner account`
              }
              className="text-[11px] font-semibold text-cyan-700 hover:text-cyan-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Copy from partner account {canCopyFromPartner ? `(${partnerAccountSuburbs.length})` : ""} →
            </button>
          </div>
          <textarea
            data-testid="pws-partner-landing-input-suburbs"
            value={form.service_suburbs}
            onChange={(e) => setField("service_suburbs", e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            rows={2}
            placeholder="Noosa, Tewantin, Cooroy, Pomona, Eumundi"
          />
          {!isEdit && !form.service_suburbs.trim() && canCopyFromPartner && (
            <p
              data-testid="pws-partner-landing-suburbs-autoinherit-hint"
              className="text-[11px] text-slate-500 mt-1"
            >
              Leave blank to auto-inherit {partnerAccountSuburbs.length} suburbs from
              the partner&apos;s account on save.
            </p>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Phone (optional)</label>
          <input
            data-testid="pws-partner-landing-input-phone"
            value={form.phone_display}
            onChange={(e) => setField("phone_display", e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            maxLength={40}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email (optional)</label>
          <input
            data-testid="pws-partner-landing-input-email"
            type="email"
            value={form.email_display}
            onChange={(e) => setField("email_display", e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            maxLength={200}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Hero headline (optional)</label>
          <input
            data-testid="pws-partner-landing-input-hero-headline"
            value={form.hero_headline}
            onChange={(e) => setField("hero_headline", e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            maxLength={200}
            placeholder="Tank Water, Bore Water… IT'S YOUR WATER"
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Blank uses the default AWARE brand headline. Use a line break (Shift+Enter is fine) to split into two lines.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Hero subheadline (optional)</label>
          <input
            data-testid="pws-partner-landing-input-hero-subheadline"
            value={form.hero_subheadline}
            onChange={(e) => setField("hero_subheadline", e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            maxLength={200}
            placeholder="SO KNOW WHAT'S IN IT, BEFORE YOU DRINK IT!"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Hero intro override (optional)</label>
          <textarea
            data-testid="pws-partner-landing-input-intro"
            value={form.hero_intro_override}
            onChange={(e) => setField("hero_intro_override", e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            rows={3}
            maxLength={800}
            placeholder="Leave blank to use the default AWARE + PWS intro copy."
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Franchisee name (optional)</label>
          <input
            data-testid="pws-partner-landing-input-franchisee-name"
            value={form.franchisee_name}
            onChange={(e) => setField("franchisee_name", e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            placeholder="e.g. Nathan Wilson"
            maxLength={120}
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Shown next to the franchisee photo in the &ldquo;Your Local Water Specialists&rdquo; card on the public landing page.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Franchisee photo (optional)</label>
          <div className="mt-1 flex items-start gap-3">
            {form.franchisee_photo_url && (
              <img
                src={form.franchisee_photo_url}
                alt="Franchisee headshot"
                className="w-16 h-16 rounded-full object-cover border border-slate-200 flex-shrink-0"
                data-testid="pws-partner-landing-photo-preview"
              />
            )}
            <div className="flex-1 min-w-0">
              {/* SCWT-FRANCHISEE-PHOTO-DURABILITY-P2 · Feb 2026 —
                  the previous editable text <input maxLength={500}>
                  silently truncated the ~6 MB base64 data URL back
                  to 500 chars on the next form Save, blanking the
                  photo in production. Replaced with a read-only
                  summary + explicit Clear button so accidental edits
                  can't wipe a good upload. Admin still sees the
                  underlying URL for auditing, just can't mangle it. */}
              {form.franchisee_photo_url ? (
                <div
                  data-testid="pws-partner-landing-input-photo"
                  className="h-10 w-full rounded-md border border-slate-300 bg-slate-50 px-3 text-xs text-slate-600 flex items-center justify-between gap-2"
                >
                  <span className="truncate font-mono">
                    {form.franchisee_photo_url.startsWith("data:")
                      ? `Uploaded image · ${Math.round(form.franchisee_photo_url.length / 1024)} KB · base64`
                      : form.franchisee_photo_url}
                  </span>
                  <button
                    type="button"
                    onClick={() => setField("franchisee_photo_url", "")}
                    className="flex-shrink-0 text-[11px] font-semibold text-rose-600 hover:text-rose-700 underline"
                    data-testid="pws-partner-landing-photo-clear"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div
                  data-testid="pws-partner-landing-input-photo-empty"
                  className="h-10 w-full rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 text-xs text-slate-400 flex items-center"
                >
                  No headshot uploaded yet — use the button below.
                </div>
              )}
              <div className="mt-2 flex items-center gap-3">
                {initial?.id && (
                  <label
                    data-testid="pws-partner-landing-photo-upload"
                    className="inline-flex items-center gap-1.5 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white text-xs font-semibold px-3 py-1.5 cursor-pointer transition-colors"
                  >
                    Upload file
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const { data } = await api.post(
                            `/admin/pws-partner-landing/pages/${initial.id}/photo`,
                            fd,
                            { headers: { "Content-Type": "multipart/form-data" } },
                          );
                          setField("franchisee_photo_url", data.franchisee_photo_url);
                          toast.success("Headshot uploaded — remember to Save the page.");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Upload failed.");
                        } finally {
                          if (e.target) e.target.value = "";
                        }
                      }}
                    />
                  </label>
                )}
                <p className="text-[11px] text-slate-500">
                  {initial?.id
                    ? "JPG, PNG or WebP · Max 5 MB. Uploaded files land at /api/uploads/franchisee-photos/…"
                    : "Save the page first, then reopen to upload a photo."}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* SCWT-HERO-BG · Feb 2026 — Hero background image uploader.
            Same MIME/size guardrails as the headshot, stored inline
            (base64 data URL) so pod restarts can never wipe it. */}
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Hero background image (optional)</label>
          <div className="mt-1 flex items-start gap-3">
            {form.hero_bg_url && (
              <img
                src={form.hero_bg_url}
                alt="Hero background preview"
                className="w-32 h-20 rounded-md object-cover border border-slate-200 flex-shrink-0"
                data-testid="pws-partner-landing-hero-bg-preview"
              />
            )}
            <div className="flex-1 min-w-0">
              {form.hero_bg_url ? (
                <div
                  data-testid="pws-partner-landing-input-hero-bg"
                  className="h-10 w-full rounded-md border border-slate-300 bg-slate-50 px-3 text-xs text-slate-600 flex items-center justify-between gap-2"
                >
                  <span className="truncate font-mono">
                    {form.hero_bg_url.startsWith("data:")
                      ? `Uploaded image · ${Math.round(form.hero_bg_url.length / 1024)} KB · base64`
                      : form.hero_bg_url}
                  </span>
                  <button
                    type="button"
                    onClick={() => setField("hero_bg_url", "")}
                    className="flex-shrink-0 text-[11px] font-semibold text-rose-600 hover:text-rose-700 underline"
                    data-testid="pws-partner-landing-hero-bg-clear"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div
                  data-testid="pws-partner-landing-input-hero-bg-empty"
                  className="h-10 w-full rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 text-xs text-slate-400 flex items-center"
                >
                  No hero image uploaded yet — the storefront will fall back to the AWARE gradient.
                </div>
              )}
              <div className="mt-2 flex items-center gap-3">
                {initial?.id && (
                  <label
                    data-testid="pws-partner-landing-hero-bg-upload"
                    className="inline-flex items-center gap-1.5 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white text-xs font-semibold px-3 py-1.5 cursor-pointer transition-colors"
                  >
                    Upload hero image
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const { data } = await api.post(
                            `/admin/pws-partner-landing/pages/${initial.id}/hero-bg`,
                            fd,
                            { headers: { "Content-Type": "multipart/form-data" } },
                          );
                          setField("hero_bg_url", data.hero_bg_url);
                          toast.success("Hero image uploaded — remember to Save the page.");
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Upload failed.");
                        } finally {
                          if (e.target) e.target.value = "";
                        }
                      }}
                    />
                  </label>
                )}
                <p className="text-[11px] text-slate-500">
                  Landscape JPG/PNG/WebP · max 5 MB · shown behind the &ldquo;Professional Water Testing&rdquo; headline with a dark overlay.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Testimonial quote (optional)</label>
          <textarea
            data-testid="pws-partner-landing-input-testimonial-quote"
            value={form.testimonial_quote}
            onChange={(e) => setField("testimonial_quote", e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            rows={3}
            maxLength={1000}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Testimonial author (optional)</label>
          <input
            data-testid="pws-partner-landing-input-testimonial-author"
            value={form.testimonial_author}
            onChange={(e) => setField("testimonial_author", e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            maxLength={120}
          />
        </div>

        {/* Feb 2026 storefront rebuild — full editors for services /
            bios / FAQs / testimonials / before-after / SEO / preview */}
        <StorefrontFieldsPanel form={form} setField={setField} initial={initial} />

        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Status</label>
          <select
            data-testid="pws-partner-landing-input-status"
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>
      <div className="flex items-center gap-3 pt-2">
        <Button
          type="submit"
          disabled={saving}
          data-testid="pws-partner-landing-form-save"
          className="bg-cyan-600 hover:bg-cyan-700 text-white h-10 px-5 rounded-full font-semibold"
        >
          {saving ? "Saving…" : (isEdit ? "Save changes" : "Create landing page")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          data-testid="pws-partner-landing-form-cancel"
          className="h-10 px-5 rounded-full font-semibold border-slate-300"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}


export default function AdminPwsPartnerLanding() {
  const [rows, setRows] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | row object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pagesRes, partnersRes] = await Promise.all([
        api.get("/admin/pws-partner-landing/pages"),
        api.get("/admin/pws-partner-landing/available-partners"),
      ]);
      setRows(pagesRes.data?.rows || []);
      setAvailablePartners(partnersRes.data?.rows || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't load landing pages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = async () => {
    setEditing(null);
    await load();
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete the landing page for "${row.business_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/pws-partner-landing/pages/${row.id}`);
      toast.success("Landing page deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed");
    }
  };

  const onArchive = async (row) => {
    try {
      await api.put(`/admin/pws-partner-landing/pages/${row.id}`, { status: "archived" });
      toast.success("Landing page archived");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Archive failed");
    }
  };

  const onPublish = async (row) => {
    try {
      await api.put(`/admin/pws-partner-landing/pages/${row.id}`, { status: "published" });
      toast.success("Landing page published");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Publish failed");
    }
  };

  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(publicUrl(slug));
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const copyShortLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(shortUrl(slug));
      toast.success(`Short link copied — ${shortUrl(slug)}`);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")),
    [rows],
  );

  return (
    <PortalShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Link to="/admin/dashboard" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="pws-partner-landing-admin-h1">
              PWS Partner Landing Pages
            </h1>
            <p className="mt-2 text-slate-600 max-w-2xl">
              AWARE-branded public pages, one per PWS franchisee. Every booking CTA on these pages carries the partner&apos;s <code className="bg-slate-100 px-1 rounded text-[11px]">?p=&lt;slug&gt;</code> attribution automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={load}
              data-testid="pws-partner-landing-refresh"
              className="h-10 px-4 rounded-full border-slate-300 font-semibold"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            {editing === null && (
              <Button
                onClick={() => setEditing("new")}
                data-testid="pws-partner-landing-new"
                className="bg-cyan-600 hover:bg-cyan-700 text-white h-10 px-5 rounded-full font-semibold"
              >
                <Plus className="w-4 h-4 mr-1.5" /> New landing page
              </Button>
            )}
          </div>
        </div>

        {editing !== null && (
          <div className="mb-8">
            <LandingForm
              initial={editing === "new" ? null : editing}
              availablePartners={availablePartners}
              onCancel={() => setEditing(null)}
              onSaved={onSaved}
            />
          </div>
        )}

        <AnalyticsPanel availablePartners={availablePartners} />

        {loading ? (
          <div data-testid="pws-partner-landing-loading" className="text-slate-500 text-sm">Loading…</div>
        ) : sortedRows.length === 0 ? (
          <div data-testid="pws-partner-landing-empty" className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
            <div className="font-semibold text-slate-900">No landing pages yet.</div>
            <div className="mt-1 text-sm text-slate-600">Create your first AWARE-branded PWS partner page.</div>
          </div>
        ) : (
          <div data-testid="pws-partner-landing-list" className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Slug · Partner</th>
                  <th className="text-left px-4 py-3 font-semibold">Region</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Attribution</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr key={r.id} data-testid={`pws-partner-landing-row-${r.slug}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 align-top">
                      <div className="font-mono text-[12px] text-slate-500">/pws-partner/{r.slug}</div>
                      <div className="font-semibold text-slate-900">{r.business_name}</div>
                      <button
                        type="button"
                        onClick={() => copyShortLink(r.slug)}
                        data-testid={`pws-partner-landing-shortlink-${r.slug}`}
                        title="Copy the SMS-friendly short link — safe to text, never truncated"
                        className="mt-1 inline-flex items-center gap-1 font-mono text-[11px] text-cyan-700 bg-cyan-50 border border-cyan-200 px-1.5 py-0.5 rounded hover:bg-cyan-100 transition-colors"
                      >
                        <Copy className="w-3 h-3" /> /p/{r.slug}
                      </button>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-700">{r.region_label || "—"}</td>
                    <td className="px-4 py-3 align-top"><StatusChip status={r.status} /></td>
                    <td className="px-4 py-3 align-top">
                      {r.partner_slug ? (
                        <span className="inline-flex items-center gap-1 text-[12px] font-mono bg-cyan-50 text-cyan-800 border border-cyan-200 px-2 py-0.5 rounded">
                          ?p={r.partner_slug}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                          <XCircle className="w-3 h-3" /> partner not active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link to={`/pws-partner/${r.slug}`} target="_blank" rel="noopener">
                          <Button variant="outline" size="sm" data-testid={`pws-partner-landing-view-${r.slug}`} className="h-8 rounded-full border-slate-300">
                            <ExternalLink className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                        </Link>
                        <Button variant="outline" size="sm" onClick={() => copyLink(r.slug)} data-testid={`pws-partner-landing-copy-${r.slug}`} className="h-8 rounded-full border-slate-300">
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/pws-partner-landing/pages/${r.id}/qr.svg`} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" data-testid={`pws-partner-landing-qr-${r.slug}`} className="h-8 rounded-full border-slate-300">
                            <QrCode className="w-3.5 h-3.5" />
                          </Button>
                        </a>
                        <Button variant="outline" size="sm" onClick={() => setEditing(r)} data-testid={`pws-partner-landing-edit-${r.slug}`} className="h-8 rounded-full border-slate-300">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {r.status !== "published" ? (
                          <Button variant="outline" size="sm" onClick={() => onPublish(r)} data-testid={`pws-partner-landing-publish-${r.slug}`} className="h-8 rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                            <Send className="w-3.5 h-3.5 mr-1" /> Publish
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => onArchive(r)} data-testid={`pws-partner-landing-archive-${r.slug}`} className="h-8 rounded-full border-amber-300 text-amber-700 hover:bg-amber-50">
                            <Archive className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => onDelete(r)} data-testid={`pws-partner-landing-delete-${r.slug}`} className="h-8 rounded-full border-rose-300 text-rose-700 hover:bg-rose-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PortalShell>
  );
}


// ─────────────────────────────────────────────────────────────────
// Analytics panel (Feb 2026 · 4c)
//   Shows the landing-page funnel per partner + date range. Everything
//   is aggregate — customer PII is NEVER surfaced here (customer
//   emails live on `quote_leads`, not on analytics events).
// ─────────────────────────────────────────────────────────────────
function AnalyticsPanel({ availablePartners }) {
  const [range, setRange] = useState("30d");
  const [partnerId, setPartnerId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);

  const bounds = useMemo(() => {
    if (range === "all") return { start: "", end: "" };
    const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
    const end = new Date();
    const start = new Date(); start.setDate(end.getDate() - days);
    return {
      start: start.toISOString().slice(0, 19),
      end:   end.toISOString().slice(0, 19),
    };
  }, [range]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      if (bounds.start) qs.set("start", bounds.start);
      if (bounds.end)   qs.set("end",   bounds.end);
      if (partnerId)    qs.set("partner_id", partnerId);
      const res = await api.get(`/admin/pws-partner-landing/analytics?${qs}`);
      setData(res.data);
    } catch (err) {
      setError(formatApiError(err) || "Couldn't load analytics.");
    } finally {
      setLoading(false);
    }
  }, [bounds.start, bounds.end, partnerId]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setCsvBusy(true);
    try {
      const qs = new URLSearchParams();
      if (bounds.start) qs.set("start", bounds.start);
      if (bounds.end)   qs.set("end",   bounds.end);
      if (partnerId)    qs.set("partner_id", partnerId);
      await fetchAndOpenAuthed({
        path: `/admin/pws-partner-landing/analytics/export.csv?${qs}`,
        mode: "download",
        loadingMessage: "Preparing analytics CSV…",
      });
    } finally {
      setCsvBusy(false);
    }
  };

  const t = data?.totals || {};

  return (
    <section
      data-testid="pws-partner-landing-analytics"
      className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-cyan-700" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
              Landing page analytics
            </div>
            <div className="text-sm text-slate-600">
              Aggregate views, QR scans, CTA clicks, quote requests and conversion rate — no customer PII shown.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="pws-partner-landing-analytics-range"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="h-9 rounded-lg border border-slate-300 px-2 text-sm bg-white"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          <select
            data-testid="pws-partner-landing-analytics-partner"
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
            className="h-9 rounded-lg border border-slate-300 px-2 text-sm bg-white max-w-[220px]"
          >
            <option value="">All partners</option>
            {availablePartners.map((p) => (
              <option key={p.id} value={p.id}>{p.business_name}</option>
            ))}
          </select>
          <Button
            variant="outline"
            onClick={load}
            data-testid="pws-partner-landing-analytics-refresh"
            className="h-9 px-3 rounded-lg border-slate-300 text-slate-700"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
          <Button
            onClick={exportCsv}
            disabled={csvBusy || loading || !(data?.partners?.length)}
            data-testid="pws-partner-landing-analytics-csv"
            className="h-9 px-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm"
          >
            {csvBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
            CSV
          </Button>
        </div>
      </div>

      {error && (
        <div data-testid="pws-partner-landing-analytics-error" className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
        <AnalyticsTile label="Views" value={t.landing_page_view} icon={Eye} tone="cyan" />
        <AnalyticsTile label="Unique views" value={t.unique_views} icon={Eye} tone="cyan" />
        <AnalyticsTile label="QR scans" value={t.qr_scan} icon={QrCode} tone="slate" />
        <AnalyticsTile label="CTA clicks" value={t.booking_cta_click} icon={MousePointerClick} tone="amber" />
        <AnalyticsTile label="Quote requests" value={t.quote_request_submitted} icon={Send} tone="emerald" />
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-xs" data-testid="pws-partner-landing-analytics-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Partner</th>
              <th className="text-right px-3 py-2">Views</th>
              <th className="text-right px-3 py-2">Unique</th>
              <th className="text-right px-3 py-2">QR</th>
              <th className="text-right px-3 py-2">CTA</th>
              <th className="text-right px-3 py-2">Quotes</th>
              <th className="text-right px-3 py-2">CTA %</th>
              <th className="text-right px-3 py-2">Quote %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.partners || []).map((p) => (
              <tr key={p.partner_id} data-testid={`pws-partner-landing-analytics-row-${p.partner_id}`}>
                <td className="px-3 py-2 font-semibold text-slate-800">
                  {p.business_name}
                  {p.region && <span className="ml-2 text-[11px] text-slate-500">{p.region}</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{p.landing_page_view}</td>
                <td className="px-3 py-2 text-right tabular-nums">{p.unique_views}</td>
                <td className="px-3 py-2 text-right tabular-nums">{p.qr_scan}</td>
                <td className="px-3 py-2 text-right tabular-nums">{p.booking_cta_click}</td>
                <td className="px-3 py-2 text-right tabular-nums">{p.quote_request_submitted}</td>
                <td className="px-3 py-2 text-right tabular-nums text-amber-700">{p.cta_conversion_rate}%</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">{p.quote_conversion_rate}%</td>
              </tr>
            ))}
            {!loading && !(data?.partners || []).length && (
              <tr>
                <td colSpan="8" className="px-3 py-6 text-center text-slate-500 text-sm">
                  No activity yet in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-[11px] text-slate-500">
        Read-only. Events are anonymous — visitor IDs are session-scoped in the browser and never linked to a customer identity.
      </div>
    </section>
  );
}

function AnalyticsTile({ label, value, icon: Icon, tone }) {
  const toneMap = {
    cyan:    "border-cyan-100 bg-cyan-50/70 text-cyan-800",
    slate:   "border-slate-200 bg-slate-50 text-slate-700",
    amber:   "border-amber-100 bg-amber-50/70 text-amber-800",
    emerald: "border-emerald-100 bg-emerald-50/70 text-emerald-800",
  };
  return (
    <div className={`rounded-xl border p-3 flex items-center gap-3 ${toneMap[tone] || toneMap.slate}`}>
      {Icon && <Icon className="w-4 h-4 opacity-70" />}
      <div>
        <div className="text-lg font-bold tabular-nums leading-none">{value ?? 0}</div>
        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70 mt-0.5">{label}</div>
      </div>
    </div>
  );
}
